import {
  Box,
  Typography,
  Paper,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  MenuItem,
  Alert,
  InputAdornment,
} from '@mui/material';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { useState, useEffect, useMemo } from 'react';
import { FormTextField, FormSelect, MaterialAutocompleteField } from '@/shared/components/forms';
import { PhotoUpload } from './PhotoUpload';
import { getUnits } from '@/shared/api/unitsService';
import type { Unit } from '@/shared/types/unit';
import { formatPriceWithAbbr } from '@/shared/utils/formatters';

/**
 * Product section component for the offer creation form.
 * Allows users to select product from materials catalog and enter details.
 *
 * Features:
 * - Required product selection from materials catalog (autocomplete)
 * - Unit price input with auto-calculated total price
 * - Quantity and unit inputs (units fetched from API)
 * - Condition toggle (Neuf/Occasion)
 * - Photo upload component
 * - Mobile-optimized with proper validation
 */

import type { OfferFormData } from '../types/offer.types';

interface ProductSectionProps {
  index: number;
  canRemove?: boolean;
  onRemove?: () => void;
  existingPhotos?: { id: string; url: string; thumbnailUrl?: string }[];
  onDeleteExistingPhoto?: (id: string) => void;
}

export function ProductSection({
  index,
  canRemove = false,
  onRemove,
  existingPhotos,
  onDeleteExistingPhoto,
}: ProductSectionProps) {
  const { t, i18n } = useTranslation();
  const {
    control,
    formState: { errors },
    trigger,
    getValues,
    watch,
  } = useFormContext<OfferFormData>();
  const [units, setUnits] = useState<Unit[]>([]);

  const productErrors = errors?.products?.[index];
  const unitPriceField = `products.${index}.unitPrice` as const;
  const quantityField = `products.${index}.quantity` as const;
  const unitIdField = `products.${index}.unitId` as const;

  // Watch unitPrice and quantity for total price calculation
  const unitPrice = watch(unitPriceField);
  const quantity = watch(quantityField);

  // Calculate total price with abbreviations
  const totalPrice = useMemo(() => {
    const price = Number.parseFloat(unitPrice);
    const qty = Number.parseFloat(quantity);

    if (Number.isNaN(price) || Number.isNaN(qty) || price < 0 || qty <= 0) {
      return '-';
    }

    const total = price * qty;
    return formatPriceWithAbbr(total);
  }, [unitPrice, quantity]);
  const [loadingUnits, setLoadingUnits] = useState(true);
  const [unitsError, setUnitsError] = useState<string | null>(null);

  // Fetch units on component mount
  useEffect(() => {
    const fetchUnits = async () => {
      try {
        setLoadingUnits(true);
        setUnitsError(null);
        const data = await getUnits();
        setUnits(data);
      } catch (error) {
        console.error('Failed to fetch units:', error);
        setUnitsError(t('publish.product.unitsLoadError'));
      } finally {
        setLoadingUnits(false);
      }
    };

    fetchUnits();
  }, [t]);

  // Revalidate unitId after units are loaded (if it has a value)
  useEffect(() => {
    if (!loadingUnits && units.length > 0) {
      const unitIdValue = getValues(unitIdField);
      if (unitIdValue) {
        trigger(unitIdField);
      }
    }
  }, [loadingUnits, units, trigger, getValues, unitIdField]);

  return (
    <Paper
      sx={{
        p: { xs: 2, sm: 3 },
        mb: 3,
        backgroundColor: 'background.paper',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      }}
      elevation={0}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
          <Box
            sx={{
              backgroundColor: '#FFE500',
              borderRadius: '16px',
              width: 44,
              height: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Inventory2Icon sx={{ color: '#1E293B', fontSize: 22 }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
            {t('publish.product.titleWithIndex', { index: index + 1 })}
          </Typography>
        </Box>
        {canRemove && onRemove ? (
          <IconButton
            type="button"
            onClick={onRemove}
            data-testid={`publish-product-remove-${index}`}
            aria-label={t('publish.product.removeProduct')}
            size="small"
            sx={{
              color: 'error.main',
              border: '1px solid',
              borderColor: 'error.light',
            }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        ) : null}
      </Box>

      {/* Product selection */}
      <Box sx={{ mb: 2.5 }}>
        <MaterialAutocompleteField
          categoryName={`products.${index}.materialCategory`}
          subcategoryName={`products.${index}.materialSubcategory`}
          materialIdName={`products.${index}.materialId`}
          control={control}
          categoryLabel={t('publish.product.categoryLabel')}
          subcategoryLabel={t('publish.product.subcategoryLabel')}
          categoryPlaceholder={t('publish.product.categoryPlaceholder')}
          subcategoryPlaceholder={t('publish.product.subcategoryPlaceholder')}
          required
          categoryError={!!productErrors?.materialCategory}
          categoryHelperText={productErrors?.materialCategory?.message as string}
          subcategoryError={!!productErrors?.materialSubcategory}
          subcategoryHelperText={productErrors?.materialSubcategory?.message as string}
          requiredMessage={t('publish.product.required')}
          subcategoryRequiredMessage={t('publish.product.subcategoryRequired')}
          categoryId={`material-category-input-${index}`}
          subcategoryId={`material-subcategory-input-${index}`}
          categoryAriaLabel={t('publish.product.categoryLabel')}
          subcategoryAriaLabel={t('publish.product.subcategoryLabel')}
        />
      </Box>

      {/* Unit Price and Total Price - Side by Side */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2.5, alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1 }}>
          <Controller
            name={`products.${index}.unitPrice`}
            control={control}
            rules={{
              required: t('publish.product.unitPriceRequired'),
              validate: value => {
                const num = Number.parseFloat(value);
                if (Number.isNaN(num) || num < 0) {
                  return t('publish.product.unitPriceInvalid');
                }
                if (num > 9999.99) {
                  return t('publish.product.unitPriceMax');
                }
                // Check decimal places (max 2)
                const decimalPart = value.toString().split('.')[1];
                if (decimalPart && decimalPart.length > 2) {
                  return t('publish.product.unitPriceDecimalPlaces');
                }
                return true;
              },
            }}
            render={({ field }) => (
              <FormTextField
                {...field}
                onChange={e => {
                  const value = e.target.value;
                  // Allow empty value
                  if (value === '') {
                    field.onChange(value);
                    return;
                  }
                  // Check if value has more than 2 decimal places
                  const decimalPart = value.split('.')[1];
                  if (decimalPart && decimalPart.length > 2) {
                    // Prevent input - don't update field
                    return;
                  }
                  // Allow valid input
                  field.onChange(value);
                }}
                id={`unit-price-input-${index}`}
                label={t('publish.product.unitPriceLabel')}
                placeholder={t('publish.product.unitPricePlaceholder')}
                required
                type="number"
                slotProps={{
                  htmlInput: {
                    min: '0',
                    step: 'any',
                    inputMode: 'decimal',
                    onWheel: (e: React.WheelEvent<HTMLInputElement>) => e.currentTarget.blur(),
                    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault();
                    },
                  },
                  input: {
                    endAdornment: <InputAdornment position="end">€</InputAdornment>,
                  },
                }}
                error={!!productErrors?.unitPrice}
                helperText={productErrors?.unitPrice?.message as string}
                aria-label={t('publish.product.unitPriceLabel')}
              />
            )}
          />
        </Box>

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Typography
            component="label"
            sx={{
              fontSize: '0.875rem',
              fontWeight: 500,
              color: 'rgba(0, 0, 0, 0.6)',
              mb: 1,
            }}
          >
            {t('publish.product.totalPriceLabel')}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              minHeight: '56px',
            }}
          >
            <Typography
              id="total-price-display"
              sx={{
                fontSize: '1.125rem',
                fontWeight: 600,
                color: 'primary.main',
              }}
              aria-label={t('publish.product.totalPriceLabel')}
              aria-live="polite"
              aria-atomic="true"
            >
              {totalPrice}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Quantity and Unit - Side by Side */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2.5, alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1 }}>
          <Controller
            name={`products.${index}.quantity`}
            control={control}
            rules={{
              required: t('publish.product.quantityRequired'),
              validate: value => {
                const num = Number.parseFloat(value);
                if (Number.isNaN(num) || num <= 0) {
                  return t('publish.product.quantityInvalid');
                }
                if (num > 9999) {
                  return t('publish.product.quantityMax');
                }
                // Check decimal places (max 2)
                const decimalPart = value.toString().split('.')[1];
                if (decimalPart && decimalPart.length > 2) {
                  return t('publish.product.quantityDecimalPlaces');
                }
                return true;
              },
            }}
            render={({ field }) => (
              <FormTextField
                {...field}
                onChange={e => {
                  const value = e.target.value;
                  // Allow empty value
                  if (value === '') {
                    field.onChange(value);
                    return;
                  }
                  // Check if value has more than 2 decimal places
                  const decimalPart = value.split('.')[1];
                  if (decimalPart && decimalPart.length > 2) {
                    // Prevent input - don't update field
                    return;
                  }
                  // Allow valid input
                  field.onChange(value);
                }}
                id={`quantity-input-${index}`}
                label={t('publish.product.quantityLabel')}
                placeholder={t('publish.product.quantityPlaceholder')}
                required
                type="number"
                disabled={loadingUnits || !!unitsError}
                slotProps={{
                  htmlInput: {
                    min: '0.01',
                    step: 'any',
                    inputMode: 'decimal',
                    onWheel: (e: React.WheelEvent<HTMLInputElement>) => e.currentTarget.blur(),
                    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault();
                    },
                  },
                }}
                error={!!productErrors?.quantity}
                helperText={productErrors?.quantity?.message as string}
                aria-label={t('publish.product.quantityLabel')}
              />
            )}
          />
        </Box>

        <Box sx={{ flex: 1 }}>
          <Controller
            name={`products.${index}.unitId`}
            control={control}
            rules={{
              required: t('publish.product.unitRequired'),
              validate: (value: string) => {
                if (!value || value.trim().length === 0) {
                  return t('publish.product.unitRequired');
                }
                // Skip validation if units are still loading
                if (loadingUnits || units.length === 0) {
                  return true;
                }
                const validUnitIds = units.map(unit => unit.id);
                if (!validUnitIds.includes(value)) {
                  return t('publish.product.unitInvalid');
                }
                return true;
              },
            }}
            render={({ field }) => (
              <FormSelect
                {...field}
                id={`unit-input-${index}`}
                label={t('publish.product.unitLabel')}
                required
                disabled={loadingUnits || !!unitsError}
                error={!!productErrors?.unitId}
                helperText={productErrors?.unitId?.message as string}
                aria-label={t('publish.product.unitLabel')}
              >
                <MenuItem value="" disabled>
                  <Typography sx={{ color: 'text.disabled' }}>
                    {loadingUnits ? t('common.loading') : t('publish.product.unitPlaceholder')}
                  </Typography>
                </MenuItem>
                {units.map(unit => (
                  <MenuItem key={unit.id} value={unit.id}>
                    {i18n.language === 'en' ? unit.labelEn : unit.labelFr}
                  </MenuItem>
                ))}
              </FormSelect>
            )}
          />
        </Box>
      </Box>

      {/* Error Alert */}
      {unitsError && (
        <Alert severity="error" sx={{ mb: 2.5 }}>
          {unitsError}
        </Alert>
      )}

      {/* Condition - État */}
      <Box sx={{ mb: 2.5 }}>
        <Typography
          sx={{
            display: 'block',
            fontSize: '14px',
            fontWeight: 500,
            color: 'text.primary',
            mb: 1,
          }}
        >
          {t('publish.product.conditionLabel')}
        </Typography>
        <Controller
          name={`products.${index}.condition`}
          control={control}
          defaultValue="used"
          render={({ field }) => (
            <ToggleButtonGroup
              {...field}
              exclusive
              onChange={(_event, newValue) => {
                if (newValue !== null) {
                  field.onChange(newValue);
                }
              }}
              fullWidth
              aria-label={t('publish.product.conditionLabel')}
              sx={{
                display: 'flex',
                '& .MuiToggleButton-root': {
                  py: 0.5,
                  border: '1px solid #E5E7EB',
                  textTransform: 'none',
                  fontSize: '16px',
                  fontWeight: 500,
                  color: 'text.primary',
                  flex: 1,
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    borderColor: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                },
              }}
            >
              <ToggleButton value="new" aria-label={t('publish.product.conditionNew')}>
                {t('publish.product.conditionNew')}
              </ToggleButton>
              <ToggleButton value="used" aria-label={t('publish.product.conditionUsed')}>
                {t('publish.product.conditionUsed')}
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        />
      </Box>

      {/* Photo Upload */}
      <Box sx={{ mb: 2 }}>
        <Controller
          name={`products.${index}.photos`}
          control={control}
          defaultValue={[]}
          render={({ field }) => (
            <PhotoUpload
              photos={field.value}
              onChange={field.onChange}
              maxPhotos={Number(import.meta.env.VITE_MAX_PHOTOS_PER_PRODUCT) || 3}
              maxSizeMB={Number(import.meta.env.VITE_MAX_PHOTO_SIZE_MB) || 10}
              existingPhotos={existingPhotos}
              onDeleteExisting={onDeleteExistingPhoto}
            />
          )}
        />
      </Box>
    </Paper>
  );
}
